import { DashboardKpi } from "@/components/admin/order-board";
import AdminPageHeader from "@/components/admin/page-header";
import { useLoadFeedback } from "@/hooks/use-load-feedback";
import { PageState } from "@/components/ui/page-state";
import React, { useEffect, useMemo, useState } from "react";

const formatShortDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric" });
};
import { toast } from "sonner";
import { CalendarDays, Coins, FileText, Printer, RefreshCcw, TicketPercent, Wallet } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api, { formatIDR } from "@/lib/api";
import { useOutlet } from "@/context/OutletContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const STATUS_LABELS = {
  pending: "Menunggu",
  preparing: "Dipersiapkan",
  served: "Disajikan",
  completed: "Selesai",
  cancelled: "Dibatalkan",
};

const PAYMENT_STATUS_LABELS = {
  unpaid: "Belum Lunas",
  paid: "Lunas",
  refunded: "Refund",
};

// formatIDR memakai non-breaking space (U+00A0) setelah "Rp" sehingga seluruh
// "Rp 226.150" dianggap satu kata utuh — wrap terjadi di tengah angka.
// Ganti ke spasi biasa agar pemutusan (kalau terjadi) rapi setelah "Rp".
const formatIDRWrap = (n) => formatIDR(n).replace(/\u00A0/g, " ");

export default function AdminReports() {
  const { activeOutlet } = useOutlet();
  const outletId = activeOutlet?.id;
  const { loading, refreshing, error: loadError, run } = useLoadFeedback(outletId);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [report, setReport] = useState(null);

  useEffect(() => {
    const today = new Date();
    const to = today.toISOString().slice(0, 10);
    const from = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    setFromDate(from);
    setToDate(to);
  }, []);

  const loadReport = React.useCallback(() => run(async () => {
    if (!outletId) return;
    try {
      const { data } = await api.get("/reports/sales", {
        params: { outlet_id: outletId, from_date: fromDate, to_date: toDate },
      });
      setReport(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal memuat laporan");
      throw error;
    }
  }), [outletId, fromDate, toDate, run]);

  useEffect(() => {
    if (outletId && fromDate && toDate) {
      loadReport();
    }
  }, [outletId, fromDate, toDate, loadReport]);

  const daySummary = useMemo(() => report?.by_day || [], [report]);
  const topItems = useMemo(() => report?.top_items || [], [report]);
  const byTable = useMemo(() => report?.by_table || [], [report]);
  const byStatus = useMemo(() => report?.by_status || [], [report]);
  const byPayment = useMemo(() => report?.by_payment || [], [report]);
  const hourData = useMemo(
    () => (report?.by_hour || []).map((h) => ({ ...h, label: `${String(h.hour).padStart(2, "0")}:00` })),
    [report]
  );

  const avgOrderValue = useMemo(() => {
    if (!report?.total_orders) return null;
    return report.total_revenue / report.total_orders;
  }, [report]);

  const cancelRate = useMemo(() => {
    const totalAll = byStatus.reduce((sum, s) => sum + s.orders, 0);
    if (!totalAll) return null;
    const cancelled = byStatus.find((s) => s.status === "cancelled")?.orders || 0;
    return (cancelled / totalAll) * 100;
  }, [byStatus]);

  const buildPrintWindowHTML = () => {
    const outletName = activeOutlet?.name ?? "-";
    const period = `${formatShortDate(fromDate)} - ${formatShortDate(toDate)}`;
    const esc = (v) => String(v ?? "").replace(/[&<>]/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[m]));

    const table = (title, headers, rows) => `
      <section class="block">
        <h2>${esc(title)}</h2>
        <table>
          <thead><tr>${headers.map((h) => `<th>${esc(h)}</th>`).join("")}</tr></thead>
          <tbody>${
            rows.length
              ? rows.map((r) => `<tr>${r.map((x) => `<td>${esc(x)}</td>`).join("")}</tr>`).join("")
              : `<tr><td class="empty" colspan="${headers.length}">Tidak ada data untuk rentang tanggal ini.</td></tr>`
          }</tbody>
        </table>
      </section>`;

    const summary = [
      ["Total Pendapatan", formatIDR(report.total_revenue ?? 0)],
      ["Pendapatan Lunas", formatIDR(report.total_paid_revenue ?? 0)],
      ["Pendapatan Belum Lunas", formatIDRWrap(report.total_unpaid_revenue ?? 0)],
      ["Total Pesanan", String(report.total_orders ?? 0)],
      ["Nilai Pesanan Rata-rata", formatIDR(avgOrderValue ?? 0)],
      ["Pesanan Dibatalkan", String(byStatus.find((s) => s.status === "cancelled")?.orders ?? 0)],
    ];
    if (cancelRate !== null) summary.push(["Tingkat Pembatalan", `${cancelRate.toFixed(1)}%`]);

    const sections = [
      table("Ringkasan Penjualan", ["Indikator", "Nilai"], summary),
      table("Status Pesanan", ["Status", "Pesanan", "Pendapatan"], byStatus.map((s) => [STATUS_LABELS[s.status] || s.status, s.orders, formatIDR(s.revenue)])),
      table("Status Pembayaran", ["Status", "Pesanan", "Pendapatan"], byPayment.map((p) => [PAYMENT_STATUS_LABELS[p.payment_status] || p.payment_status, p.orders, formatIDR(p.revenue)])),
      table("Menu Terlaris", ["Menu", "Qty", "Pendapatan"], topItems.map((it) => [it.name, it.quantity, formatIDR(it.revenue)])),
      table("Pesanan per Jam", ["Jam", "Pesanan", "Pendapatan"], hourData.map((h) => [h.label, h.orders, formatIDR(h.revenue)])),
      table("Kinerja Meja", ["Meja", "Pesanan", "Pendapatan"], byTable.map((t) => [t.table_number, t.orders, formatIDR(t.revenue)])),
      table("Ringkasan Harian", ["Tanggal", "Pesanan", "Pendapatan"], daySummary.map((d) => [formatShortDate(d.date), d.orders, formatIDR(d.revenue)])),
    ].join("");

    return `<!DOCTYPE html>
<html lang="id">
  <head>
    <meta charset="utf-8" />
    <title>Laporan Penjualan - ${esc(outletName)}</title>
    <style>
      * { box-sizing: border-box; }
      body { margin: 0; padding: 24px; font-family: system-ui, -apple-system, "Segoe UI", sans-serif; color: #1f2937; background: #fff; }
      header { border-bottom: 2px solid #386641; padding-bottom: 12px; margin-bottom: 20px; }
      h1 { margin: 0; font-size: 22px; color: #386641; }
      .meta { margin-top: 4px; font-size: 13px; color: #4b5563; }
      section.block { margin-bottom: 22px; break-inside: avoid; }
      h2 { font-size: 15px; color: #386641; margin: 0 0 8px; }
      table { width: 100%; border-collapse: collapse; font-size: 13px; }
      th, td { border: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; }
      th { background: #f4f4f0; color: #386641; font-weight: 600; }
      td.empty { text-align: center; color: #6b7280; padding: 12px; }
      .sign { display: flex; justify-content: space-between; margin-top: 40px; font-size: 13px; }
      .sign div { text-align: center; width: 45%; }
      .sign .line { margin-top: 56px; border-top: 1px solid #000; }
      @media print { @page { margin: 12mm; } body { padding: 0; } }
    </style>
  </head>
  <body>
    <header>
      <h1>Laporan Penjualan</h1>
      <div class="meta">Outlet: ${esc(outletName)} &middot; Periode: ${esc(period)}</div>
    </header>
    ${sections}
    <div class="sign">
      <div><p>Dibuat oleh,</p><div class="line"></div><p>Kasir / Admin</p></div>
      <div><p>Disetujui oleh,</p><div class="line"></div><p>Manajer</p></div>
    </div>
  </body>
</html>`;
  };

  const handlePrint = () => {
    if (!report) return;
    const win = window.open("", "_blank", "width=960,height=720");
    if (!win) {
      toast.error("Gagal membuka jendela cetak. Izinkan pop-up untuk mencetak laporan.");
      return;
    }
    win.document.open();
    win.document.write(buildPrintWindowHTML());
    win.document.close();
    win.focus();
    // ponytail: blank popup needs a tick before layout settles; ceiling = swap to onafterprint if flaky.
    win.onafterprint = () => win.close();
    setTimeout(() => win.print(), 250);
  };


  return (
    <div aria-busy={refreshing} className="admin-page space-y-6" data-testid="admin-reports-page">

      <AdminPageHeader className="no-print" title="Laporan & Analitik" description="Ringkasan penjualan berdasarkan outlet dan rentang tanggal." />
      <div className="admin-filter-panel no-print">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Dari</span>
            <Input aria-label="Tanggal mulai laporan" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="bg-white" />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Sampai</span>
            <Input aria-label="Tanggal akhir laporan" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="bg-white" />
          </label>
          <Button onClick={loadReport} className="h-11 self-end rounded-lg col-span-2 sm:col-span-1" disabled={refreshing || !outletId}>
            <RefreshCcw className="w-4 h-4 mr-2" /> Muat ulang
          </Button>
          <Button onClick={handlePrint} variant="outline" className="h-11 self-end rounded-lg col-span-2 sm:col-span-1 bg-white" disabled={!report || refreshing} data-testid="export-report-btn">
            <Printer className="w-4 h-4 mr-2" /> Cetak
          </Button>
        </div>
      </div>

      {(loading || loadError) && <PageState kind={loading ? "loading" : "error"} title={loading ? "Memuat laporan" : "Laporan belum dapat dimuat"} description={loadError} onRetry={loadError ? loadReport : undefined} />}

      <div id="reports-print-root" className="space-y-6">
        <div className="admin-report-kpis">
          <DashboardKpi label="Total Pesanan" value={report ? report.total_orders : "-"} icon={FileText} testid="report-total-card" />
          <DashboardKpi label="Total Pendapatan" value={report ? formatIDRWrap(report.total_revenue) : "-"} currency icon={Coins} testid="report-revenue-card" />
          <DashboardKpi label="Belum Lunas" value={report ? formatIDRWrap(report.total_unpaid_revenue ?? 0) : "-"} hint={report ? `${byPayment.find((p) => p.payment_status === "unpaid")?.orders ?? 0} pesanan` : "-"} currency icon={Wallet} testid="report-unpaid-card" />
          <DashboardKpi label="Total Diskon" value={report ? formatIDRWrap(report.total_discount || 0) : "-"} currency icon={TicketPercent} testid="report-discount-card" />
          <DashboardKpi label="Rata-rata / Pesanan" value={avgOrderValue !== null ? formatIDRWrap(avgOrderValue) : "-"} currency icon={Coins} testid="report-average-card" />
          <DashboardKpi label="Rentang Tanggal" value={report ? report.from_date : "-"} hint={report ? `sampai ${report.to_date}` : "-"} currency icon={CalendarDays} testid="report-range-card" />
        </div>

        <Tabs defaultValue="day" className="w-full">
          <TabsList className="bg-white border border-border no-print">
            <TabsTrigger value="day">Ringkasan Harian</TabsTrigger>
            <TabsTrigger value="items">Item Terlaris</TabsTrigger>
            <TabsTrigger value="hours">Jam Sibuk</TabsTrigger>
            <TabsTrigger value="tables">Per Meja</TabsTrigger>
          </TabsList>

          <TabsContent value="day">
            <div className="surface-card p-5">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="font-display text-xl font-bold">Ringkasan Harian</h2>
                  <p className="text-sm text-muted-foreground">Tampilan per tanggal dalam periode yang dipilih.</p>
                </div>
              </div>
              <div className="admin-table-scroll overflow-x-auto" tabIndex={0} role="region" aria-label="Ringkasan harian, geser untuk melihat semua kolom">
                <table className="w-full text-sm">
                  <thead className="bg-accent/50 border-b border-border text-left">
                    <tr>
                      <th className="p-3 font-medium">Tanggal</th>
                      <th className="p-3 font-medium">Pesanan</th>
                      <th className="p-3 font-medium">Pendapatan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {daySummary.length === 0 ? (
                      <tr><td colSpan={3} className="p-6 text-center text-muted-foreground">Tidak ada data untuk rentang tanggal ini.</td></tr>
                    ) : daySummary.map((day) => (
                      <tr key={day.date} className="border-b border-border last:border-0">
                        <td className="p-3">{day.date}</td>
                        <td className="p-3">{day.orders}</td>
                        <td className="p-3">{formatIDR(day.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="items">
            <div className="surface-card p-5">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="font-display text-xl font-bold">Item Terlaris</h2>
                  <p className="text-sm text-muted-foreground">10 menu dengan jumlah terjual terbanyak (order batal tidak dihitung).</p>
                </div>
              </div>
              <div className="admin-table-scroll overflow-x-auto" tabIndex={0} role="region" aria-label="Item terlaris, geser untuk melihat semua kolom">
                <table className="w-full text-sm">
                  <thead className="bg-accent/50 border-b border-border text-left">
                    <tr>
                      <th className="p-3 font-medium">#</th>
                      <th className="p-3 font-medium">Menu</th>
                      <th className="p-3 font-medium">Qty Terjual</th>
                      <th className="p-3 font-medium">Pendapatan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topItems.length === 0 ? (
                      <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">Tidak ada data untuk rentang tanggal ini.</td></tr>
                    ) : topItems.map((item, idx) => (
                      <tr key={item.menu_item_id || idx} className="border-b border-border last:border-0">
                        <td className="p-3">{idx + 1}</td>
                        <td className="p-3">{item.name}</td>
                        <td className="p-3">{item.quantity}</td>
                        <td className="p-3">{formatIDR(item.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="hours">
            <div className="surface-card p-5">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="font-display text-xl font-bold">Jam Sibuk</h2>
                  <p className="text-sm text-muted-foreground">Jumlah pesanan berdasarkan jam pemesanan.</p>
                </div>
              </div>
              <div className="h-72 no-print">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hourData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={1} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value, name) => (name === "orders" ? [value, "Pesanan"] : [formatIDR(value), "Pendapatan"])} />
                    <Bar dataKey="orders" fill="#c2410c" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="admin-table-scroll overflow-x-auto mt-4" tabIndex={0} role="region" aria-label="Jam sibuk, geser untuk melihat semua kolom">
                <table className="w-full text-sm">
                  <thead className="bg-accent/50 border-b border-border text-left">
                    <tr>
                      <th className="p-3 font-medium">Jam</th>
                      <th className="p-3 font-medium">Pesanan</th>
                      <th className="p-3 font-medium">Pendapatan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hourData.filter((h) => h.orders > 0).length === 0 ? (
                      <tr><td colSpan={3} className="p-6 text-center text-muted-foreground">Tidak ada data untuk rentang tanggal ini.</td></tr>
                    ) : hourData.filter((h) => h.orders > 0).map((h) => (
                      <tr key={h.hour} className="border-b border-border last:border-0">
                        <td className="p-3">{h.label}</td>
                        <td className="p-3">{h.orders}</td>
                        <td className="p-3">{formatIDR(h.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="tables">
            <div className="surface-card p-5">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="font-display text-xl font-bold">Pendapatan per Meja</h2>
                  <p className="text-sm text-muted-foreground">Kontribusi tiap meja terhadap penjualan.</p>
                </div>
              </div>
              <div className="admin-table-scroll overflow-x-auto" tabIndex={0} role="region" aria-label="Pendapatan per meja, geser untuk melihat semua kolom">
                <table className="w-full text-sm">
                  <thead className="bg-accent/50 border-b border-border text-left">
                    <tr>
                      <th className="p-3 font-medium">Meja</th>
                      <th className="p-3 font-medium">Pesanan</th>
                      <th className="p-3 font-medium">Pendapatan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byTable.length === 0 ? (
                      <tr><td colSpan={3} className="p-6 text-center text-muted-foreground">Tidak ada data untuk rentang tanggal ini.</td></tr>
                    ) : byTable.map((t) => (
                      <tr key={t.table_number} className="border-b border-border last:border-0">
                        <td className="p-3">Meja {t.table_number}</td>
                        <td className="p-3">{t.orders}</td>
                        <td className="p-3">{formatIDR(t.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="surface-card p-5">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="font-display text-xl font-bold">Status Pesanan</h2>
              <p className="text-sm text-muted-foreground">
                Rekap seluruh pesanan termasuk yang dibatalkan.
                {cancelRate !== null ? ` Tingkat pembatalan: ${cancelRate.toFixed(1)}%.` : ""}
              </p>
            </div>
          </div>
          <div className="admin-table-scroll overflow-x-auto" tabIndex={0} role="region" aria-label="Status pesanan, geser untuk melihat semua kolom">
            <table className="w-full text-sm">
              <thead className="bg-accent/50 border-b border-border text-left">
                <tr>
                  <th className="p-3 font-medium">Status</th>
                  <th className="p-3 font-medium">Pesanan</th>
                  <th className="p-3 font-medium">Nilai</th>
                </tr>
              </thead>
              <tbody>
                {byStatus.length === 0 ? (
                  <tr><td colSpan={3} className="p-6 text-center text-muted-foreground">Tidak ada data untuk rentang tanggal ini.</td></tr>
                ) : byStatus.map((s) => (
                  <tr key={s.status} className="border-b border-border last:border-0">
                    <td className="p-3">{STATUS_LABELS[s.status] || s.status}</td>
                    <td className="p-3">{s.orders}</td>
                    <td className="p-3">{formatIDR(s.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="hidden print:flex justify-between mt-16 px-8 text-sm">
          <div className="text-center">
            <p>Dibuat oleh,</p>
            <div className="mt-16 border-t border-black w-48 mx-auto"></div>
            <p className="mt-1">Kasir / Admin</p>
          </div>
          <div className="text-center">
            <p>Disetujui oleh,</p>
            <div className="mt-16 border-t border-black w-48 mx-auto"></div>
            <p className="mt-1">Manajer</p>
          </div>
        </div>
      </div>
    </div>
  );
}
